import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { LuEye } from "react-icons/lu";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import api from "../../utils/api";
import { useNavigate } from "react-router";
import { IoSearchSharp } from "react-icons/io5";

function InactiveEmployees() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [searchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize] = useState(8);

  const baseUrl = process.env.REACT_APP_API_URL;

  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const navigate = useNavigate();

  const applySearch = () => {
    navigate(`?search=${searchInput}`);
    setCurrentPage(1);
  };

  const fetchEmployees = async (page = 1, size = pageSize) => {
    setLoading(true);
    try {

      const searchTerm = searchParams.get("search") || "a";

      const response = await api.get(`/api/deactivated-employees`, {
        params: {
          page: page - 1,
          size,
          search: searchTerm.trim(),
        },
      });

      const data = response.data;
      console.log("Fetched employees data:", data);

      setEmployees(data.content || []);
      setTotalPages(data.totalPages || 0);
      setCurrentPage((data.currentPage ?? page - 1) + 1);

    } catch (error) {
      console.error("Error fetching employees", error);
    }
    setLoading(false);
  };

  // Trigger fetch on page change or search param change



  useEffect(() => {
    fetchEmployees(currentPage);
  }, [currentPage]);


  useEffect(() => {
    setCurrentPage(1); // Reset to first page
    fetchEmployees(1);
  }, [searchParams]);

  const openModal = (employee) => {
    setSelectedEmployee(employee);
    setShowModal(true);
  };

  return (
    <div className="sm:p-6 sm:mt-auto mt-8 mb-20 w-full mx-auto">
      {/* Header */}
      <motion.div
        className="flex justify-between items-center mb-4 bg-white rounded-2xl p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <h2 className="text-xl xs:text-2xl font-bold">Inactive(old) Employees</h2>
        <div className="relative">
          <input
            type="text"
            placeholder="Search employee..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}   // only updates local state
            onKeyDown={(e) => {
              if (e.key === 'Enter') applySearch();             // apply on Enter
            }}
            className="pl-9 pr-4 py-2 text-sm rounded-full border border-slate-200 bg-slate-50 focus:outline-none focus:border-slate-400 w-56"
          />
          <IoSearchSharp
            onClick={applySearch}                               // apply on icon click
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm cursor-pointer hover:text-slate-600"
          />
        </div>

      </motion.div>

      {/* Table */}
      <motion.div
        className="hidden sm:block bg-white rounded-2xl shadow-md overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-white border-b border-slate-200">
              <tr>
                <th className="px-3 py-4 text-sm font-semibold text-slate-600 text-left">SL No</th>
                <th className="px-3 py-4 text-sm font-semibold text-slate-600 text-left">Employee ID</th>
                <th className="px-3 py-4 text-sm font-semibold text-slate-600 text-left">Employee Name</th>
                <th className="px-3 py-4 text-sm font-semibold text-slate-600 text-left">Date of Joining</th>
                <th className="px-3 py-4 text-sm font-semibold text-slate-600 text-left">Last Working Day</th>
                <th className="px-3 py-4 text-sm font-semibold text-slate-600 text-left">Experience (yrs)</th>
                <th className="px-3 py-4 text-sm font-semibold text-slate-600 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center">
                    <div className="flex justify-center items-center">
                      <svg className="animate-spin h-8 w-8 text-slate-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                      </svg>
                    </div>
                  </td>
                </tr>
              ) : employees.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-6">No inactive employees found</td>
                </tr>
              ) : (
                employees.map((emp, index) => (
                  <tr key={emp.id} className="hover:bg-slate-50 transition-all">
                    <td className="px-3 py-2 text-sm">{(currentPage - 1) * pageSize + index + 1}</td>
                    <td className="px-3 py-2 text-sm ">{emp.employeeId}</td>
                    <td className="px-3 py-2 text-sm ">{emp.employeeName}</td>
                    <td className="px-3 py-2 text-sm">{emp.dateOfJoining}</td>
                    <td className="px-3 py-2 text-sm">{emp.dateOfLeaving}</td>
                    <td className="px-3 py-2 text-sm">{emp.experience?.toFixed(2)}</td>
                    <td className="px-3 py-2 text-sm">
                      <button
                        onClick={() => openModal(emp)}
                        className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                      >
                        <LuEye className="text-xl" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {employees.length > 0 && (
            <div className="px-3 py-2 text-sm border-t border-slate-100">
              <Stack spacing={2} className="flex justify-end">
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(_, page) => setCurrentPage(page)}
                  variant="outlined"
                  shape="full"
                  size="small"
                  showFirstButton
                  showLastButton
                  sx={{
                    "& .MuiPaginationItem-root": {
                      color: "#475569",
                      borderColor: "#e2e8f0",
                    },
                    "& .Mui-selected": {
                      backgroundColor: "#475569 !important",
                      color: "#fff",
                    },
                  }}
                />
              </Stack>
            </div>
          )}
        </div>
      </motion.div>

      {/* Modal */}
      {showModal && selectedEmployee && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-2xl shadow-lg w-[450px]">
            <h3 className="text-lg font-bold mb-4">Employee Details</h3>
            <div className="space-y-2 text-sm">
              <p><strong>Name:</strong> {selectedEmployee.employeeName}</p>
              <p><strong>Date of Joining:</strong> {selectedEmployee.dateOfJoining}</p>
              <p><strong>Date of Leaving:</strong> {selectedEmployee.dateOfLeaving}</p>
              <p><strong>Experience:</strong> {selectedEmployee.experience?.toFixed(2)} years</p>
              <p><strong>Reason:</strong> {selectedEmployee.reason}</p>
              <p><strong>UAN No:</strong> {selectedEmployee.uanNo}</p>
              <p><strong>Department:</strong> {selectedEmployee.department}</p>
              <p><strong>Contact Number:</strong> {selectedEmployee.cantactNumber}</p>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 rounded-lg border"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default InactiveEmployees;
